import type {
  DeviceCode,
  EventHeartRate,
  HrvPreprocessingExport,
  LfHfStatus,
  PolarPpiSample,
  PolarProductId,
  RrSource,
  TachogramSample,
} from '@types';

const POLAR_PRODUCT_IDS: ReadonlySet<string> = new Set([
  'polar_360',
  'polar_loop',
  'polar_h10',
]);

const RR_SOURCES: ReadonlySet<string> = new Set([
  'ppi',
  'hr_derived',
  'ecg_rr',
]);

const LF_HF_STATUSES: ReadonlySet<string> = new Set([
  'unavailable',
  'low_quality',
  'warming_up',
  'provisional',
  'valid',
]);

const PPI_REJECT_REASONS: ReadonlySet<string> = new Set([
  'out_of_physiological_range',
  'quality_rejected',
]);

const LF_HF_INTERVAL_KINDS: ReadonlySet<string> = new Set([
  'accepted',
  'quality_rejected',
  'out_of_physiological_range',
]);

const TACHOGRAM_KINDS: ReadonlySet<string> = new Set([
  'accepted',
  'quality_rejected',
]);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const readOptionalNumber = (
  value: unknown
): number | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (isFiniteNumber(value)) return value;
  return undefined;
};

const readOptionalBoolean = (
  value: unknown
): boolean | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === 'boolean') return value;
  return undefined;
};

const readOptionalString = (value: unknown): string | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === 'string') return value;
  return undefined;
};

const readPolarProductId = (
  value: unknown
): PolarProductId | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === 'string' && POLAR_PRODUCT_IDS.has(value)) {
    return value as PolarProductId;
  }
  return undefined;
};

const readRrSource = (value: unknown): RrSource | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === 'string' && RR_SOURCES.has(value)) {
    return value as RrSource;
  }
  return undefined;
};

const readLfHfStatus = (value: unknown): LfHfStatus | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === 'string' && LF_HF_STATUSES.has(value)) {
    return value as LfHfStatus;
  }
  return undefined;
};

const readHrvMethod = (
  value: unknown
): 'periodogram' | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value === 'periodogram') return 'periodogram';
  return undefined;
};

/** Finite number array, or null/undefined. Invalid → undefined (omit). */
const readNumberArray = (value: unknown): number[] | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (!Array.isArray(value)) return undefined;
  const numbers = value.filter(isFiniteNumber);
  return numbers.length === value.length ? numbers : undefined;
};

const readHzBand = (
  value: unknown
): [number, number] | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (
    Array.isArray(value) &&
    value.length === 2 &&
    isFiniteNumber(value[0]) &&
    isFiniteNumber(value[1])
  ) {
    return [value[0], value[1]];
  }
  return undefined;
};

const isPolarPpiSample = (value: unknown): value is PolarPpiSample => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const sample = value as Record<string, unknown>;
  if (!isFiniteNumber(sample.ppiMs) || !isFiniteNumber(sample.timestamp)) {
    return false;
  }
  if (
    sample.errorEstimate !== null &&
    !isFiniteNumber(sample.errorEstimate)
  ) {
    return false;
  }
  if (
    sample.rejectReason !== undefined &&
    sample.rejectReason !== null &&
    !(
      typeof sample.rejectReason === 'string' &&
      PPI_REJECT_REASONS.has(sample.rejectReason)
    )
  ) {
    return false;
  }
  if (
    sample.kind !== undefined &&
    !(typeof sample.kind === 'string' && LF_HF_INTERVAL_KINDS.has(sample.kind))
  ) {
    return false;
  }
  if (sample.accepted !== undefined && typeof sample.accepted !== 'boolean') {
    return false;
  }
  if (
    sample.includedInTimeline !== undefined &&
    typeof sample.includedInTimeline !== 'boolean'
  ) {
    return false;
  }
  if (
    sample.includedInTachogram !== undefined &&
    typeof sample.includedInTachogram !== 'boolean'
  ) {
    return false;
  }
  if (
    sample.correctedPpiMs !== undefined &&
    sample.correctedPpiMs !== null &&
    !isFiniteNumber(sample.correctedPpiMs)
  ) {
    return false;
  }
  return true;
};

const readPpiSamples = (
  value: unknown
): PolarPpiSample[] | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (!Array.isArray(value)) return undefined;
  if (!value.every(isPolarPpiSample)) return undefined;
  return value;
};

const isTachogramSample = (value: unknown): value is TachogramSample => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const sample = value as Record<string, unknown>;
  return (
    isFiniteNumber(sample.timeSec) &&
    isFiniteNumber(sample.rawPpiMs) &&
    isFiniteNumber(sample.correctedPpiMs) &&
    typeof sample.kind === 'string' &&
    TACHOGRAM_KINDS.has(sample.kind) &&
    typeof sample.jumpCorrected === 'boolean'
  );
};

const readTachogramSamples = (
  value: unknown
): TachogramSample[] | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (!Array.isArray(value)) return undefined;
  if (!value.every(isTachogramSample)) return undefined;
  return value;
};

const readHrvPreprocessing = (
  value: unknown
): HrvPreprocessingExport | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const prep = value as Record<string, unknown>;
  if (prep.unit !== 'ms2' || prep.method !== 'periodogram') return undefined;
  if (!isFiniteNumber(prep.samplingHz) || prep.livePublishHz !== 1) {
    return undefined;
  }
  if (
    prep.detrend !== 'mean_subtraction' ||
    prep.window !== 'hann' ||
    prep.spectrum !== 'one_sided'
  ) {
    return undefined;
  }
  if (
    !Array.isArray(prep.lfBandHz) ||
    prep.lfBandHz.length !== 2 ||
    !isFiniteNumber(prep.lfBandHz[0]) ||
    !isFiniteNumber(prep.lfBandHz[1])
  ) {
    return undefined;
  }
  if (
    !Array.isArray(prep.hfBandHz) ||
    prep.hfBandHz.length !== 2 ||
    !isFiniteNumber(prep.hfBandHz[0]) ||
    !isFiniteNumber(prep.hfBandHz[1])
  ) {
    return undefined;
  }
  if (
    typeof prep.artifactFilter !== 'object' ||
    prep.artifactFilter == null ||
    Array.isArray(prep.artifactFilter)
  ) {
    return undefined;
  }
  if (
    typeof prep.interpolation !== 'object' ||
    prep.interpolation == null ||
    Array.isArray(prep.interpolation)
  ) {
    return undefined;
  }
  return value as HrvPreprocessingExport;
};

const parseTimestampMs = (
  value: unknown,
  fallback: number | undefined
): number => {
  if (isFiniteNumber(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  if (isFiniteNumber(fallback)) return fallback;
  return Date.now();
};

/** Strip leading `v` for display only (`v1.2.2` → `1.2.2`). */
export const formatAppVersionForDisplay = (
  appVersion: string | undefined | null
): string | null => {
  if (appVersion == null || appVersion === '') return null;
  return appVersion.replace(/^v/i, '');
};

export type MapPolarHeartRateEventInput = {
  payload: Record<string, unknown>;
  messageId: string;
  messageClientId?: string | null;
  messageTimestamp?: number;
};

/**
 * Maps an Ably heartRate message (Polar or legacy) into an EventHeartRate.
 * Returns null when `hr` / `heartRate` is missing or not a finite number.
 */
export const mapPolarHeartRateEvent = (
  input: MapPolarHeartRateEventInput
): EventHeartRate | null => {
  const { payload, messageId, messageClientId, messageTimestamp } = input;

  const hrRaw = payload.heartRate ?? payload.hr;
  if (!isFiniteNumber(hrRaw)) return null;

  const lfRaw = payload.lf ?? payload.lfPower;
  const hfRaw = payload.hf ?? payload.hfPower;
  const hrvRaw = payload.hrv;

  const lf =
    lfRaw === null ? null : isFiniteNumber(lfRaw) ? lfRaw : 0;
  const hf =
    hfRaw === null ? null : isFiniteNumber(hfRaw) ? hfRaw : 0;
  const hrv =
    hrvRaw === null ? null : isFiniteNumber(hrvRaw) ? hrvRaw : 0;

  const deviceCode = String(
    payload.code ?? payload.clientId ?? messageClientId ?? 'unknown'
  ) as DeviceCode;

  const eventTimestamp = parseTimestampMs(
    payload.timestamp ?? payload.date,
    messageTimestamp
  );

  const event: EventHeartRate = {
    id: messageId,
    deviceCode,
    hr: hrRaw,
    hrv,
    lf,
    hf,
    timestamp: eventTimestamp,
  };

  const deviceId = readOptionalString(payload.deviceId);
  if (deviceId !== undefined) event.deviceId = deviceId;

  const deviceModel = readPolarProductId(payload.deviceModel);
  if (deviceModel !== undefined) event.deviceModel = deviceModel;

  const lfPower = readOptionalNumber(payload.lfPower);
  if (lfPower !== undefined) event.lfPower = lfPower;

  const hfPower = readOptionalNumber(payload.hfPower);
  if (hfPower !== undefined) event.hfPower = hfPower;

  const lfHfRatio = readOptionalNumber(payload.lfHfRatio);
  if (lfHfRatio !== undefined) event.lfHfRatio = lfHfRatio;

  const lfHfValid = readOptionalBoolean(payload.lfHfValid);
  if (lfHfValid !== undefined) event.lfHfValid = lfHfValid;

  const lfHfStatus = readLfHfStatus(payload.lfHfStatus);
  if (lfHfStatus !== undefined) event.lfHfStatus = lfHfStatus;

  const lfHfAcceptedRatio = readOptionalNumber(payload.lfHfAcceptedRatio);
  if (lfHfAcceptedRatio !== undefined) {
    event.lfHfAcceptedRatio = lfHfAcceptedRatio;
  }

  const lfHfMaxArtifactGapSec = readOptionalNumber(
    payload.lfHfMaxArtifactGapSec
  );
  if (lfHfMaxArtifactGapSec !== undefined) {
    event.lfHfMaxArtifactGapSec = lfHfMaxArtifactGapSec;
  }

  const lfHfComputedAt = readOptionalNumber(payload.lfHfComputedAt);
  if (lfHfComputedAt !== undefined) event.lfHfComputedAt = lfHfComputedAt;

  const rrMs = readOptionalNumber(payload.rrMs);
  if (rrMs !== undefined) event.rrMs = rrMs;

  const rrSource = readRrSource(payload.rrSource);
  if (rrSource !== undefined) event.rrSource = rrSource;

  const ppiMs = readOptionalNumber(payload.ppiMs);
  if (ppiMs !== undefined) event.ppiMs = ppiMs;

  const ppisMs = readNumberArray(payload.ppisMs);
  if (ppisMs !== undefined) event.ppisMs = ppisMs;

  const ppiSamples = readPpiSamples(payload.ppiSamples);
  if (ppiSamples !== undefined) event.ppiSamples = ppiSamples;

  const tachogramSamples = readTachogramSamples(payload.tachogramSamples);
  if (tachogramSamples !== undefined) {
    event.tachogramSamples = tachogramSamples;
  }

  const ppiTimesSec = readNumberArray(payload.ppiTimesSec);
  if (ppiTimesSec !== undefined) event.ppiTimesSec = ppiTimesSec;

  const rawIncludedPpiMs = readNumberArray(payload.rawIncludedPpiMs);
  if (rawIncludedPpiMs !== undefined) {
    event.rawIncludedPpiMs = rawIncludedPpiMs;
  }

  const correctedPpiSeries = readNumberArray(payload.correctedPpiSeries);
  if (correctedPpiSeries !== undefined) {
    event.correctedPpiSeries = correctedPpiSeries;
  }

  const resampledSeries = readNumberArray(payload.resampledSeries);
  if (resampledSeries !== undefined) event.resampledSeries = resampledSeries;

  const includedIntervalDurationSec = readOptionalNumber(
    payload.includedIntervalDurationSec
  );
  if (includedIntervalDurationSec !== undefined) {
    event.includedIntervalDurationSec = includedIntervalDurationSec;
  }

  const tachogramDomainSec = readOptionalNumber(payload.tachogramDomainSec);
  if (tachogramDomainSec !== undefined) {
    event.tachogramDomainSec = tachogramDomainSec;
  }

  const resampledDurationSec = readOptionalNumber(
    payload.resampledDurationSec
  );
  if (resampledDurationSec !== undefined) {
    event.resampledDurationSec = resampledDurationSec;
  }

  const hrvWindowSeconds = readOptionalNumber(payload.hrvWindowSeconds);
  if (hrvWindowSeconds !== undefined) {
    event.hrvWindowSeconds = hrvWindowSeconds;
  }

  const hrvIntervalCount = readOptionalNumber(payload.hrvIntervalCount);
  if (hrvIntervalCount !== undefined) {
    event.hrvIntervalCount = hrvIntervalCount;
  }

  const hrvMethod = readHrvMethod(payload.hrvMethod);
  if (hrvMethod !== undefined) event.hrvMethod = hrvMethod;

  const hrvSamplingHz = readOptionalNumber(payload.hrvSamplingHz);
  if (hrvSamplingHz !== undefined) event.hrvSamplingHz = hrvSamplingHz;

  const livePublishHz = readOptionalNumber(payload.livePublishHz);
  if (livePublishHz !== undefined) event.livePublishHz = livePublishHz;

  const peakFrequencyHz = readOptionalNumber(payload.peakFrequencyHz);
  if (peakFrequencyHz !== undefined) {
    event.peakFrequencyHz = peakFrequencyHz;
  }

  const lfBandHz = readHzBand(payload.lfBandHz);
  if (lfBandHz !== undefined) event.lfBandHz = lfBandHz;

  const hfBandHz = readHzBand(payload.hfBandHz);
  if (hfBandHz !== undefined) event.hfBandHz = hfBandHz;

  const hrvPreprocessing = readHrvPreprocessing(payload.hrvPreprocessing);
  if (hrvPreprocessing !== undefined) {
    event.hrvPreprocessing = hrvPreprocessing;
  }

  const skinTemperatureC = readOptionalNumber(payload.skinTemperatureC);
  if (skinTemperatureC !== undefined) {
    event.skinTemperatureC = skinTemperatureC;
  }

  const contactStatus = readOptionalBoolean(payload.contactStatus);
  if (contactStatus !== undefined) event.contactStatus = contactStatus;

  const contactStatusSupported = readOptionalBoolean(
    payload.contactStatusSupported
  );
  if (contactStatusSupported !== undefined) {
    event.contactStatusSupported = contactStatusSupported;
  }

  const errorEstimate = readOptionalNumber(payload.errorEstimate);
  if (errorEstimate !== undefined) event.errorEstimate = errorEstimate;

  const blocker = readOptionalBoolean(payload.blocker);
  if (blocker !== undefined) event.blocker = blocker;

  const ppgQuality = readOptionalNumber(payload.ppgQuality);
  if (ppgQuality !== undefined) event.ppgQuality = ppgQuality;

  const correctedHr = readOptionalNumber(payload.correctedHr);
  if (correctedHr !== undefined) event.correctedHr = correctedHr;

  const rrAvailable = readOptionalBoolean(payload.rrAvailable);
  if (rrAvailable !== undefined) event.rrAvailable = rrAvailable;

  const rrsMs = readNumberArray(payload.rrsMs);
  if (rrsMs !== undefined) event.rrsMs = rrsMs;

  if (typeof payload.appVersion === 'string' && payload.appVersion !== '') {
    event.appVersion = payload.appVersion;
  }

  return event;
};
